// src/components/OfflineBanner.js
import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw, X } from 'lucide-react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';

/**
 * Banner that shows when the app is offline or has connection issues
 * Appears at the top of the screen with option to dismiss or retry
 */
const OfflineBanner = () => {
  const { isOnline, isSupabaseConnected, isFullyConnected, checkConnection } = useNetworkStatus();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Reset dismissed state when connection status changes
  useEffect(() => {
    if (isFullyConnected) {
      setIsDismissed(false);
    }
  }, [isFullyConnected]);

  // Handle retry
  const handleRetry = async () => {
    setIsRetrying(true);
    await checkConnection();
    setTimeout(() => setIsRetrying(false), 1000);
  };

  // Don't show if connected or dismissed
  if (isFullyConnected || isDismissed) {
    return null;
  }

  // Determine message based on connection state
  let message = 'You are offline';
  let subMessage = 'Some features may not work until you reconnect.';

  if (isOnline && !isSupabaseConnected) {
    message = 'Connection issue';
    subMessage = 'Having trouble connecting to the server. Your changes may not save.';
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 dark:bg-amber-600 text-white px-4 py-2 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <WifiOff className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium">{message}</p>
            <p className="text-xs opacity-90">{subMessage}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRetry}
            disabled={isRetrying}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Checking...' : 'Retry'}
          </button>

          <button
            onClick={() => setIsDismissed(true)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfflineBanner;
